"use client";

import { Shield, Zap, Database, Globe, Award, Users } from "@/components/icons";
import { Images } from "@/assests";
import type { Stat, Milestone, TeamMember, WhyItem } from "./types";
import { STATS } from "@/lib/constants";

export const stats: Stat[] = [
  { value: STATS.countries, label: "Countries covered" },
  { value: STATS.ips, label: "IP addresses" },
  { value: "UPTIME_PLACEHOLDER", label: "Uptime guarantee" },
  { value: "thousands", label: "Customers served" },
];

export const milestones: Milestone[] = [
  {
    year: "2020",
    title: "PROXY KNOW",
    description:
      "Abed Elez published PROXY KNOW, the first comprehensive guide to proxy technology. The research behind it exposed a fundamental problem: proxy networks were expensive, fragile, and built to serve providers, not users.",
  },
  {
    year: "2022",
    title: "Proxidize Founded",
    description:
      "That realization became a company. Proxidize launched with a mission to make proxy infrastructure accessible to everyone, starting with proprietary mobile proxy hardware designed, manufactured, and managed in-house.",
  },
  {
    year: "2023",
    title: "Managed Mobile Proxies",
    description:
      "Proxidize launched its managed mobile proxy service for enterprise customers. Premium pooled mobile IPs across dozens of US locations delivered as a fully managed cloud service, with no third-party dependencies.",
  },
  {
    year: "2024",
    title: "Public Launch",
    description:
      "After overwhelmingly positive feedback from enterprise clients, Proxidize opened its managed mobile proxies to all users in December 2024. Same managed cloud service, same quality, now available to everyone.",
  },
  {
    year: "2025",
    title: "SOC 2 & ISO 27001",
    description:
      "Proxidize achieved SOC 2 Type II attestation and ISO 27001 certification, independently validating the security controls and operational standards behind the network.",
  },
  {
    year: "2026",
    title: "Residential Proxies",
    description: `Proxidize expanded beyond mobile with the launch of its residential proxy network. ${STATS.ips} ethically sourced IPs across ${STATS.countries} countries, with city and ISP-level targeting and full bandwidth rollover.`,
  },
];

export const teamMembers: TeamMember[] = [
  { name: "Abed Elez", role: "CEO", image: Images.team.abedElezz, initials: "AE" },
  {
    name: "Daniel Cantillo",
    role: "Technical Support Engineer",
    image: Images.team.danielCantillo,
    initials: "DC",
  },
  {
    name: "Fawaz (David) Al-Ghzawi",
    role: "Success and Support Team Lead",
    image: Images.team.fawazAlghzawi,
    initials: "FA",
  },
  {
    name: "Josh (Ahmed) Othman",
    role: "Technical Support Engineer",
    image: Images.team.josh,
    initials: "J",
  },
  {
    name: "Mahmoud Ababneh",
    role: "Multimedia Designer",
    image: Images.team.mahmoudAbabneh,
    initials: "MA",
  },
  {
    name: "Maya Abualez",
    role: "QA Engineer",
    image: Images.team.mayaAbuelezz,
    initials: "MA",
  },
  {
    name: "Muhannad Abdulla",
    role: "Video & Motion Graphic Designer",
    image: Images.team.muhannadAbdulla,
    initials: "MA",
  },
  {
    name: "Omar Hussein",
    role: "Software Engineer",
    image: Images.team.omarHussein,
    initials: "OH",
  },
  {
    name: "Osama Ibrahim",
    role: "Engineering Team Lead",
    image: Images.team.osamaIbrahim,
    initials: "OI",
  },
  {
    name: "Yamen Alazzam",
    role: "Software Engineer",
    image: Images.team.yamenAlazzam,
    initials: "YA",
  },
  {
    name: "Yazan Sharawi",
    role: "GTM Engineer",
    image: Images.team.yazanSharawi,
    initials: "YS",
  },
  {
    name: "Yazid Kurdi",
    role: "Senior Software Engineer",
    image: Images.team.yazidKurdi,
    initials: "YK",
  },
  {
    name: "Zeid Abughazaleh",
    role: "Content Writer",
    image: Images.team.zeidAbughazaleh,
    initials: "ZA",
  },
];

export const whyItems: WhyItem[] = [
  {
    icon: Globe,
    title: `${STATS.countries} Countries`,
    description:
      "Global Coverage Residential IPs across 195+ countries with city and ISP-level targeting. Mobile IPs across dozens of locations worldwide with city and ASN/ISP precision.",
  },
  {
    icon: Shield,
    title: "SOC 2 & ISO 27001 Certified",
    description:
      "Independently attested and certified. The security and operational standards your team expects before signing.",
  },
  {
    icon: Zap,
    title: "Fast Provisioning",
    description:
      "Deploy a proxy pool in minutes, not days. Our API-first architecture means no waiting for manual setup.",
  },
  {
    icon: Database,
    title: "Transparent Pricing",
    description: "Simple per-GB and per-proxy plans with no commitments and no contracts.",
  },
  {
    icon: Award,
    title: "UPTIME_PLACEHOLDER",
    description:
      "Enterprise-grade infrastructure with redundant routing means your pipelines never go dark.",
  },
  {
    icon: Users,
    title: "Dedicated Support",
    description:
      "Real engineers — not bots — available around the clock to solve your most complex proxy challenges.",
  },
];
